/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react'
import { TextField as MuiTextField, TextFieldProps as MuiTextFieldProps } from '@mui/material'
import styled from '@emotion/styled'
import { Colors, Shapes, Typographies, useTypography, themeSpacing } from 'src/components/theme'
import { RecoilState, useRecoilState } from 'recoil'

type TextFieldVariant = 'normal' | 'big'
type TextFieldFontSizeVariant = 'normal' | 'big'

export type TextFieldProps = {
  variant?: TextFieldVariant
  startAdornment?: React.ReactNode
  fontSizeVariant?: TextFieldFontSizeVariant
} & Omit<MuiTextFieldProps, 'variant'>

const TextFieldWrapper = styled.div<TextFieldProps>`
  & > div {
    width: ${(props: TextFieldProps) => (props.fullWidth ? '100%' : `${Shapes.textFieldWidth}px`)};
    padding-top: ${Shapes.spacing[1]}px !important;

    & div {
      background-color: ${Colors.bgWhite};
    }

    & label {
      ${(props) => useTypography(props.variant === 'big' ? Typographies.text1m : Typographies.text2)}
      transform: translate(
          ${Shapes.spacing[3]}px,
          ${(props) => (props.variant === 'big' ? 14 : 14)}px
        )
        scale(1);
      color: ${Colors.textMedium} !important;
    }

    & label.MuiInputLabel-shrink {
      ${useTypography(Typographies.caption1)}
      transform: translate(
        ${Shapes.spacing[3]}px,
          -${themeSpacing(1) / 2}px
        )
        scale(1);
    }

    & label.Mui-disabled {
      color: ${Colors.textDisabled} !important;
    }

    & label.Mui-error {
      color: ${Colors.redDark} !important;
    }

    & input {
      ${(props) => useTypography(props.variant === 'big' ? Typographies.text1m : Typographies.text1)}
      padding: ${Shapes.spacing[2]}px !important;
      box-sizing: content-box;
      color: ${Colors.textDark};
      font-size: ${({ fontSizeVariant }) => (fontSizeVariant === 'big' ? '18px' : undefined)};
    }

    & label.MuiInputLabel-root[data-shrink='false'] {
      font-size: ${({ fontSizeVariant }) => (fontSizeVariant === 'big' ? '18px' : undefined)};
      margin-top: ${({ fontSizeVariant }) => (fontSizeVariant === 'big' ? '6px' : undefined)};
    }

    & div.Mui-focused input {
      border-color: ${Colors.borderInputHover} !important;
      border-width: 1px !important;
      color: ${Colors.textDark};
    }

    & div.Mui-disabled input {
      color: ${Colors.textDisabled} !important;
    }

    & div.Mui-focused.Mui-error input {
      border-color: ${Colors.redDark} !important;
    }

    & div.Mui-disabled {
      background-color: ${Colors.bgDark};
    }

    & fieldset {
      top: -5px;
      padding: 0 ${Shapes.spacing[2]}px !important;
      border-color: ${Colors.borderInput};
      border-width: 1px solid !important;
      border-radius: ${Shapes.borderRadius}px;
      box-shadow: 0px 1px 2px 0px ${Colors.shadowInput};
    }

    & div.Mui-error fieldset {
      border-color: ${Colors.redDark} !important;
    }

    & div:hover fieldset {
      border-color: ${Colors.borderInputHover} !important;
      box-shadow: 0px 1px 2px 0px ${Colors.shadowInputHover};
      border-width: 1px solid !important;
    }

    & div.Mui-error:hover fieldset {
      border-color: ${Colors.redDark} !important;
    }

    & div.Mui-focused fieldset {
      border-color: ${Colors.borderInputHover} !important;
      border-width: 1px !important;
    }

    & div.Mui-error.Mui-focused fieldset {
      border-color: ${Colors.redDark} !important;
    }

    & fieldset legend span {
      ${useTypography(Typographies.caption1)}
    }

    & .MuiFormHelperText-root {
      margin-inline: ${Shapes.spacing[1]}px;
    }
    & .MuiInputBase-root {
      padding: ${Shapes.spacing[0]}px !important;
      input,
      textarea {
        padding: ${Shapes.spacing[2]}px !important;
      }
    }
  }
`

export const TextField: React.FC<TextFieldProps> = (props) => {
  const { variant = 'normal', fullWidth, fontSizeVariant = 'normal' } = props
  return (
    <TextFieldWrapper variant={variant} fullWidth={fullWidth} fontSizeVariant={fontSizeVariant}>
      <MuiTextField {...props} variant="outlined" />
    </TextFieldWrapper>
  )
}

type RecoilTextFieldProps = TextFieldProps & {
  atom: RecoilState<string>
  required?: boolean
}
export const RecoilTextField: React.FC<RecoilTextFieldProps> = (props) => {
  const { atom, required } = props
  const [val, setVal] = useRecoilState(atom)
  return (
    <TextField
      value={val}
      variant="big"
      error={required && !val}
      onChange={(e) => setVal(e.currentTarget.value)}
      {...props}
    />
  )
}
